// Email Capture Form Component
// Shows after calculation results to build email list
// Strategy: "Get your results + retirement planning checklist" (highest conversion)
// Last Updated: May 2026

import React, { useState, useId } from 'react';
import '../styles/email-capture-form.css';
import { getLeadCaptureFields } from '../utils/attribution';

import { buildBasicEmailResults } from '../utils/basicCalculatorPdfEmail.js';

// Build results payload with key metrics for email summary (privacy-safe: user's own numbers)
function buildResultsPayload(results, calculatorType, inputs) {
  const payload = { hasResults: true };
  if (calculatorType === 'basic' || calculatorType === 'advanced') {
    let finalBalance = results.finalBalance;
    let maxSustainableIncome = results.maxSustainableIncome;
    let successRate = results.successRate;
    if (calculatorType === 'basic' && results.chartData?.length) {
      const derived = buildBasicEmailResults(results.chartData, {
        maxSustainableIncome: results.maxSustainableIncome,
        successRate: results.successRate
      });
      finalBalance = derived.finalBalance;
      maxSustainableIncome = derived.maxSustainableIncome;
      successRate = derived.successRate;
    }
    if (typeof finalBalance === 'number') payload.finalBalance = finalBalance;
    if (typeof maxSustainableIncome === 'number') payload.maxSustainableIncome = maxSustainableIncome;
    if (typeof successRate === 'number') payload.successRate = successRate;
    const periodLength = inputs?.periodLength ?? inputs?.retirementPeriod;
    if (periodLength != null) payload.periodLength = periodLength;
    if (inputs?.startYear) payload.startYear = inputs.startYear;
    if (results.chartData?.length) {
      payload.pdfProjectionRows = results.chartData
        .filter((row, i, arr) => {
          const w = row.withdrawal ?? row.withdrawalFromSuper ?? 0;
          const income = row.totalIncome ?? 0;
          return w > 0 || income > 0 || i < arr.length - 1;
        })
        .slice(0, 35)
        .map((row, idx) => ({
          year: row.year ?? idx + 1,
          age: row.age ?? (inputs?.yourAge != null ? Number(inputs.yourAge) + idx : undefined),
          balance: row.endingBalance ?? row.balance ?? 0,
          withdrawal: row.withdrawal ?? row.withdrawalFromSuper ?? 0,
          pension: row.pension ?? row.agePension ?? 0,
          otherIncome: row.otherIncome ?? 0,
          totalIncome: row.totalIncome ?? 0
        }));
    }
  }
  if (calculatorType === 'accumulation') {
    if (typeof results.totalFinalBalance === 'number') payload.totalFinalBalance = results.totalFinalBalance;
    if (typeof results.avgAnnualContributions === 'number') payload.avgAnnualContributions = results.avgAnnualContributions;
    if (inputs?.retirementAge) payload.retirementAge = inputs.retirementAge;
  }
  return payload;
}

// Build inputs payload (non-sensitive metadata only)
function buildInputsPayload(inputs, calculatorType) {
  if (!inputs) {
    return { calculatorType, hasInputs: false };
  }
  return {
    calculatorType,
    hasInputs: true,
    periodLength: inputs.periodLength ?? inputs.retirementPeriod ?? null,
    retirementAge: inputs.retirementAge ?? inputs.yourAge ?? null,
    relationshipStatus: inputs.relationshipStatus ?? null,
    yourAge: inputs.yourAge ?? null,
    yourSuper: inputs.yourSuper ?? null,
    partnerAge: inputs.partnerAge ?? null,
    partnerSuper: inputs.partnerSuper ?? null,
    isHomeowner: inputs.isHomeowner ?? null,
    includePension: inputs.includePension ?? null,
    assumedReturn: inputs.assumedReturn ?? null,
    assumedInflation: inputs.assumedInflation ?? null,
    pensionGrowthRate: inputs.pensionGrowthRate ?? null,
    adminFees: inputs.adminFees ?? null,
    adminFeePercentage: inputs.adminFeePercentage ?? null,
    investmentFees: inputs.investmentFees ?? null,
    insuranceFees: inputs.insuranceFees ?? null,
    otherTaxableIncome: inputs.otherTaxableIncome ?? null,
    otherNonTaxableIncome: inputs.otherNonTaxableIncome ?? null
  };
}

function isValidEmail(value) {
  const trimmed = (value || '').trim();
  return trimmed.length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
}

// Full calculator PDFs with embedded chart images can exceed 1MB base64.
export const MAX_EMAIL_PDF_BASE64_LENGTH = 2500000;

export function normalizePdfBase64ForEmail(pdfBase64) {
  if (!pdfBase64 || typeof pdfBase64 !== 'string') return null;
  if (pdfBase64.length <= MAX_EMAIL_PDF_BASE64_LENGTH) return pdfBase64;
  return null;
}

function getCaptureEndpoints() {
  // Netlify redirect: /api/* → /.netlify/functions/* — try /api first on production
  return ['/api/capture-email', '/.netlify/functions/capture-email'];
}

async function readResponseData(response) {
  const text = await response.text();
  if (!text || !text.trim()) {
    return { data: {}, isJson: true };
  }
  const trimmed = text.trim();
  if (trimmed.startsWith('<')) {
    return { data: {}, isJson: false };
  }
  try {
    return { data: JSON.parse(text), isJson: true };
  } catch {
    return { data: {}, isJson: false };
  }
}

async function postEmailCapture(payload) {
  let lastError = null;
  const isLocal =
    typeof window !== 'undefined' &&
    /^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname);

  for (const url of getCaptureEndpoints()) {
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const { data, isJson } = await readResponseData(response);

      if (!isJson) {
        lastError = new Error(
          isLocal
            ? 'Email API not available on this dev server. Run npm start (not a static file open), or test on supercalcpro.com.au.'
            : 'Email service returned an unexpected response. Please try again in a moment.'
        );
        continue;
      }

      if (response.ok) {
        return { response, data };
      }
      lastError = new Error(data.error || data.message || `Request failed (${response.status})`);
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError || new Error('Could not reach the email service. Check your connection and try again.');
}

async function loadPdfWithTimeout(getPdfBase64, calculatorType, timeoutMs) {
  if (!getPdfBase64 || typeof getPdfBase64 !== 'function') return null;
  const ms = timeoutMs ?? (calculatorType === 'basic' ? 35000 : 8000);
  try {
    const value = getPdfBase64();
    const pdf = value && typeof value.then === 'function' ? await value : value;
    return pdf || null;
  } catch (err) {
    console.warn('PDF generation for email skipped:', err?.message || err);
    return null;
  }
}

export const EmailCaptureForm = ({ calculatorType = 'advanced', results = null, inputs = null, getPdfBase64 = null }) => {
  const inputId = useId().replace(/:/g, '');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [emailSent, setEmailSent] = useState(true);
  const [pdfAttached, setPdfAttached] = useState(true);
  const [error, setError] = useState(null);
  const [submittedEmail, setSubmittedEmail] = useState('');

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setError(null);

    const trimmedEmail = email.trim();
    if (!isValidEmail(trimmedEmail)) {
      setError('Please enter a valid email address (e.g. you@example.com).');
      return;
    }

    setIsSubmitting(true);

    try {
      if (typeof gtag !== 'undefined') {
        gtag('event', 'email_capture_attempt', {
          event_category: 'Email Capture',
          event_label: calculatorType,
          calculator_type: calculatorType,
          source: 'calculator_results'
        });
      }

      let pdfBase64 = normalizePdfBase64ForEmail(await loadPdfWithTimeout(getPdfBase64, calculatorType));

      const basePayload = {
        email: trimmedEmail,
        calculatorType,
        results: results ? buildResultsPayload(results, calculatorType, inputs) : null,
        inputs: inputs ? buildInputsPayload(inputs, calculatorType) : null,
        source: 'calculator_results',
        ...getLeadCaptureFields()
      };

      const { data } = await postEmailCapture(
        pdfBase64 ? { ...basePayload, pdfBase64 } : basePayload
      );

      setSubmittedEmail(trimmedEmail);
      setIsSubmitted(true);
      setEmailSent(data.emailSent !== false);
      setPdfAttached(data.pdfAttached === true);

      if (typeof gtag !== 'undefined') {
        gtag('event', 'email_capture_success', {
          event_category: 'Email Capture',
          event_label: calculatorType,
          calculator_type: calculatorType,
          source: 'calculator_results',
          value: 1
        });
      }
    } catch (err) {
      console.error('Email capture error:', err);
      setError(err.message || 'Something went wrong. Please try again.');

      if (typeof gtag !== 'undefined') {
        gtag('event', 'email_capture_failure', {
          event_category: 'Email Capture',
          event_label: calculatorType,
          calculator_type: calculatorType,
          source: 'calculator_results',
          error: err.message
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    if (emailSent && !pdfAttached) {
      return (
        <div className="rounded-xl border-2 border-amber-200 bg-amber-50 p-6 shadow-md">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-amber-200 flex items-center justify-center text-2xl text-amber-800" aria-hidden="true">&#9888;</div>
            <div className="flex-1 min-w-0">
              <h3 className="text-xl font-bold text-amber-900 mb-2">Checklist sent — PDF missing</h3>
              <p className="text-amber-800 mb-4">
                We emailed the checklist to <strong>{submittedEmail}</strong>, but the PDF report was not attached.
                Try again or use <strong>Download PDF</strong> on this page.
              </p>
            </div>
          </div>
        </div>
      );
    }
    if (emailSent) {
      return (
        <div className="rounded-xl border-2 border-green-200 bg-green-50 p-6 shadow-md">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-green-200 flex items-center justify-center text-2xl text-green-800" aria-hidden="true">&#10003;</div>
            <div className="flex-1 min-w-0">
              <h3 className="text-xl font-bold text-green-900 mb-2">Check your email</h3>
              <p className="text-green-800 mb-4">
                We&apos;ve sent your <strong>PDF report</strong> and the <strong>Retirement Readiness Checklist</strong> to <strong>{submittedEmail}</strong>.
              </p>
              <div className="bg-white rounded-lg p-4 border border-green-100 space-y-2 text-sm">
                <p className="font-semibold text-gray-900">What&apos;s next?</p>
                <p className="text-gray-700">
                  We may send occasional tips on retirement planning. Unsubscribe anytime.
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="rounded-xl border-2 border-amber-200 bg-amber-50 p-6 shadow-md">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-amber-200 flex items-center justify-center text-2xl text-amber-800" aria-hidden="true">&#9888;</div>
          <div className="flex-1 min-w-0">
            <h3 className="text-xl font-bold text-amber-900 mb-2">Saved, but email couldn&apos;t be sent</h3>
            <p className="text-amber-800 mb-4">
              We&apos;ve saved your details for <strong>{submittedEmail}</strong>, but the results email wasn&apos;t sent. This is usually a temporary configuration issue. Try again later or use a different email address.
            </p>
            <p className="text-sm text-amber-700">
              If it keeps happening, contact support@supercalcpro.com.au and we&apos;ll fix it.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border-2 border-blue-100 bg-gradient-to-br from-blue-50 to-white p-6 shadow-md">
      <div className="flex flex-col sm:flex-row sm:items-start gap-5">
        <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-blue-100 flex items-center justify-center text-2xl" aria-hidden="true">
          &#9993;
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-xl font-bold text-gray-900 mb-1">Get these results + retirement checklist</h3>
          <p className="text-gray-700 mb-4 text-base">
            Get your calculation results emailed as a PDF, plus our free <strong>5-Step Retirement Readiness Checklist</strong>.
          </p>

          <div className="space-y-4">
            <div className="email-capture-form">
              <label
                htmlFor={inputId}
                className="block text-sm font-semibold text-gray-900 mb-2"
              >
                Your email address
              </label>
              <input
                id={inputId}
                type="email"
                name="email"
                autoComplete="email"
                inputMode="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error) setError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
                placeholder="you@example.com"
                disabled={isSubmitting}
                aria-invalid={error ? 'true' : 'false'}
                aria-describedby={error ? `${inputId}-error` : `${inputId}-hint`}
                className="email-capture-input block w-full box-border px-4 py-3.5 text-base text-gray-900 bg-white border-2 border-gray-400 rounded-lg shadow-sm placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-600 disabled:bg-gray-100 disabled:cursor-not-allowed"
                style={{ width: '100%', minWidth: '100%', maxWidth: '100%' }}
              />
              <p id={`${inputId}-hint`} className="mt-1.5 text-xs text-gray-600">
                Type the address where you want your PDF results and checklist sent.
              </p>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                style={{ backgroundColor: isSubmitting ? '#64748b' : '#0f172a', width: '100%' }}
                className="email-capture-submit mt-3 w-full px-6 py-3.5 text-base font-bold rounded-lg text-white hover:opacity-90 active:opacity-100 disabled:cursor-wait transition shadow-md min-h-[48px] border-0 box-border"
              >
                {isSubmitting ? 'Sending…' : 'Email me my results'}
              </button>
              {error && (
                <p id={`${inputId}-error`} role="alert" className="mt-2 text-sm text-red-600 font-medium flex items-center gap-1">
                  <span aria-hidden="true">&#9888;</span>
                  <span>{error}</span>
                </p>
              )}
            </div>

            <div className="bg-white/80 rounded-lg p-4 border border-gray-100">
              <p className="text-sm font-semibold text-gray-900 mb-2">We&apos;ll send you:</p>
              <ul className="text-sm text-gray-700 space-y-1.5">
                <li className="flex items-center gap-2">
                  <span className="text-blue-600 font-bold">&#10003;</span>
                  <span>Your calculation results (PDF report)</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-blue-600 font-bold">&#10003;</span>
                  <span>5-Step Retirement Readiness Checklist</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-blue-600 font-bold">&#10003;</span>
                  <span>Weekly tips on maximizing your super</span>
                </li>
              </ul>
              <p className="text-xs text-gray-500 mt-3 italic">
                100% free &bull; No spam &bull; Unsubscribe anytime
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailCaptureForm;
